import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  format, addDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  isSameMonth, isSameDay, addMonths, subMonths, parseISO, isToday, isPast
} from 'date-fns';
import { Clock, ChevronLeft, ChevronRight, Loader2, X, Calendar, AlertTriangle, CheckCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_OF_WEEK = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS   = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const TIME_SLOTS = [
  '09:00 – 10:00',
  '10:00 – 11:00',
  '11:00 – 12:00',
  '12:00 – 13:00',
  '13:00 – 14:00',
  '14:00 – 15:00',
  '15:00 – 16:00',
  '16:00 – 17:00',
  '17:00 – 18:00',
  '18:00 – 19:00',
  '19:00 – 20:00',
];

// Build 14 upcoming dates (tomorrow + 13 more)
const buildDateRange = (anchor = new Date()) =>
  Array.from({ length: 14 }, (_, i) => format(addDays(anchor, i + 1), 'yyyy-MM-dd'));

// ─── Cell component ───────────────────────────────────────────────────────────
// defaultActive: true in date mode (no override = inherits day config = active)
const SlotCell = ({ slot, onToggle, isHoliday, loading, defaultActive = false }) => {
  if (isHoliday) {
    return (
      <div className="h-10 flex items-center justify-center rounded-xl bg-red-50 border border-red-100">
        <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
      </div>
    );
  }
  if (loading) {
    return <div className="h-10 flex items-center justify-center rounded-xl bg-slate-50 border border-slate-100">
      <Loader2 className="w-3 h-3 animate-spin text-slate-300" />
    </div>;
  }
  if (!slot) {
    if (defaultActive) {
      // No date-specific override yet — inherits day default (active). Click to disable.
      return (
        <button
          onClick={onToggle}
          className="h-10 w-full flex items-center justify-center rounded-xl font-semibold text-xs transition-all bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm shadow-emerald-200"
          title="Click to disable for this date"
        >
          ✓
        </button>
      );
    }
    return (
      <button
        onClick={onToggle}
        className="h-10 w-full flex items-center justify-center rounded-xl border border-dashed border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-all text-slate-300 hover:text-blue-400 text-xs"
        title="Click to enable"
      >
        +
      </button>
    );
  }
  return (
    <button
      onClick={onToggle}
      className={`h-10 w-full flex items-center justify-center rounded-xl font-semibold text-xs transition-all ${
        slot.is_active
          ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm shadow-emerald-200'
          : 'bg-slate-100 hover:bg-slate-200 text-slate-400 line-through'
      }`}
      title={slot.is_active ? 'Click to disable' : 'Click to enable'}
    >
      {slot.is_active ? '✓' : '✕'}
    </button>
  );
};

// ─── Calendar day cell ────────────────────────────────────────────────────────
const CalDayCell = ({ date, currentMonth, holiday, onSelect, selected }) => {
  const dateStr = format(date, 'yyyy-MM-dd');
  const inMonth = isSameMonth(date, currentMonth);
  const past = isPast(date) && !isToday(date);
  const isHoliday = !!holiday;
  const isSel = selected === dateStr;

  return (
    <button
      onClick={() => !past && onSelect(dateStr, holiday)}
      disabled={past}
      className={`relative h-10 w-full flex flex-col items-center justify-center rounded-xl text-xs font-semibold transition-all
        ${!inMonth ? 'opacity-30' : ''}
        ${past ? 'cursor-not-allowed opacity-30' : 'cursor-pointer'}
        ${isHoliday ? 'bg-red-100 text-red-700 hover:bg-red-200' : ''}
        ${isSel && !isHoliday ? 'bg-blue-600 text-white' : ''}
        ${!isHoliday && !isSel && !past && inMonth ? 'hover:bg-slate-100' : ''}
        ${isToday(date) && !isHoliday ? 'ring-2 ring-blue-400' : ''}
        ${isHoliday ? 'border border-red-200' : ''}
      `}
    >
      <span>{format(date, 'd')}</span>
      {isHoliday && <span className="absolute bottom-0.5 w-1 h-1 rounded-full bg-red-400" />}
    </button>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const AdminSlotManagement = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  // ── Slot grid state
  const [mode, setMode] = useState('day'); // 'day' | 'date'
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [togglingCell, setTogglingCell] = useState(null); // "col::timeSlot"
  const [dateRangeStart, setDateRangeStart] = useState(new Date());

  // ── Holiday calendar state
  const [holidays, setHolidays] = useState([]);
  const [loadingHolidays, setLoadingHolidays] = useState(true);
  const [calMonth, setCalMonth] = useState(new Date());
  const [selectedCalDate, setSelectedCalDate] = useState(null);
  const [reasonInput, setReasonInput] = useState('');
  const [savingHoliday, setSavingHoliday] = useState(false);
  const [deletingHoliday, setDeletingHoliday] = useState(null);

  // ── Date columns for "By Date" mode
  const dateRange = useMemo(() => buildDateRange(dateRangeStart), [dateRangeStart]);

  // ── Build slot map: { col → { timeSlot → slot doc } }
  const slotMap = useMemo(() => {
    const map = {};
    slots.forEach(s => {
      const col = s.slot_type === 'day' ? s.day_of_week : s.date;
      if (col) {
        if (!map[col]) map[col] = {};
        map[col][s.time_slot] = s;
      }
    });
    return map;
  }, [slots]);

  // ── Holiday map: { dateStr → holiday doc }
  const holidayMap = useMemo(() => {
    const m = {};
    holidays.forEach(h => { m[h.date] = h; });
    return m;
  }, [holidays]);

  useEffect(() => { initSlots(); fetchHolidays(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // First load: fetch + auto-initialize if no day-type slots exist
  const initSlots = async () => {
    setLoadingSlots(true);
    try {
      const r = await axios.get(`${API}/admin/pickup-slots`, { headers });
      const fetched = r.data;
      const daySlots = fetched.filter(s => s.slot_type === 'day');
      if (daySlots.length === 0) {
        // Single backend call initializes all 77 default slots
        const r2 = await axios.post(`${API}/admin/pickup-slots/initialize-defaults`, {}, { headers });
        setSlots(r2.data.slots);
      } else {
        setSlots(fetched);
      }
    } catch (err) {
      console.error('Slot init error:', err?.response?.data || err.message);
      toast.error('Failed to load slots');
    }
    finally { setLoadingSlots(false); }
  };

  // Silent reload — no spinner, no re-init
  const reloadSlots = async () => {
    try {
      const r = await axios.get(`${API}/admin/pickup-slots`, { headers });
      setSlots(r.data);
    } catch { /* silent */ }
  };

  const [resetting, setResetting] = useState(false);
  const handleFixAndCleanup = async () => {
    if (!window.confirm('This will clean up duplicate/legacy slots and fix any corrupted slot data. Current active/inactive settings will be preserved. Proceed?')) return;
    setResetting(true);
    try {
      const r = await axios.post(`${API}/admin/pickup-slots/initialize-defaults`, {}, { headers });
      setSlots(r.data.slots);
      toast.success('Slots cleaned and fixed successfully');
    } catch { toast.error('Failed to fix slots'); }
    finally { setResetting(false); }
  };

  const handleResetAll = async () => {
    if (!window.confirm('This will RESET ALL day-of-week slots to defaults (all enabled). Date-specific overrides are kept. Are you sure?')) return;
    setResetting(true);
    try {
      const r = await axios.post(`${API}/admin/pickup-slots/reset-defaults`, {}, { headers });
      setSlots(r.data.slots);
      toast.success('All slots reset to defaults');
    } catch { toast.error('Failed to reset slots'); }
    finally { setResetting(false); }
  };

  const fetchHolidays = async () => {
    setLoadingHolidays(true);
    try {
      const r = await axios.get(`${API}/admin/pickup-holidays`, { headers });
      setHolidays(r.data);
    } catch { toast.error('Failed to load holidays'); }
    finally { setLoadingHolidays(false); }
  };

  // Toggle a grid cell — optimistic update, no loading spinner
  const handleToggle = async (col, timeSlot) => {
    const existing = slotMap[col]?.[timeSlot];
    const cellKey = `${col}::${timeSlot}`;
    setTogglingCell(cellKey);
    try {
      if (existing) {
        // Optimistic: flip is_active immediately in local state
        setSlots(prev => prev.map(s => s.id === existing.id ? { ...s, is_active: !s.is_active } : s));
        await axios.put(`${API}/admin/pickup-slots/${existing.id}`, { is_active: !existing.is_active }, { headers });
      } else {
        // In date mode: no override means "default active", so clicking creates a disabled slot.
        // In day mode: no slot means unconfigured, clicking creates an active slot.
        const newActive = mode === 'date' ? false : true;
        const tempId = `_temp_${col}_${timeSlot}`;
        const tempSlot = { id: tempId, slot_type: mode, time_slot: timeSlot, is_active: newActive, ...(mode === 'day' ? { day_of_week: col } : { date: col }) };
        setSlots(prev => [...prev, tempSlot]); // optimistic
        const res = await axios.post(`${API}/admin/pickup-slots`, {
          time_slot: timeSlot,
          slot_type: mode,
          ...(mode === 'day' ? { day_of_week: col } : { date: col }),
          is_active: newActive,
        }, { headers });
        setSlots(prev => prev.map(s => s.id === tempId ? res.data : s));
      }
    } catch {
      toast.error('Failed to update slot');
      reloadSlots(); // revert on error
    } finally { setTogglingCell(null); }
  };

  // Calendar: select a date
  const handleCalSelect = (dateStr, existingHoliday) => {
    if (existingHoliday) {
      // Clicking a holiday date → offer to remove
      setSelectedCalDate({ date: dateStr, existing: existingHoliday });
      setReasonInput('');
    } else {
      setSelectedCalDate({ date: dateStr, existing: null });
      setReasonInput('');
    }
  };

  const handleSaveHoliday = async () => {
    if (!reasonInput.trim()) { toast.error('Please enter a reason'); return; }
    setSavingHoliday(true);
    try {
      await axios.post(`${API}/admin/pickup-holidays`, { date: selectedCalDate.date, reason: reasonInput.trim() }, { headers });
      toast.success('Holiday marked');
      setSelectedCalDate(null);
      setReasonInput('');
      fetchHolidays();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to mark holiday');
    } finally { setSavingHoliday(false); }
  };

  const handleRemoveHoliday = async (holiday) => {
    setDeletingHoliday(holiday.id);
    try {
      await axios.delete(`${API}/admin/pickup-holidays/${holiday.id}`, { headers });
      toast.success('Holiday removed');
      setSelectedCalDate(null);
      fetchHolidays();
    } catch { toast.error('Failed to remove holiday'); }
    finally { setDeletingHoliday(null); }
  };

  // ── Build calendar grid (5–6 week rows)
  const buildCalGrid = () => {
    const start = startOfWeek(startOfMonth(calMonth), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(calMonth), { weekStartsOn: 1 });
    const rows = [];
    let day = start;
    while (day <= end) {
      const row = [];
      for (let i = 0; i < 7; i++) {
        row.push(day);
        day = addDays(day, 1);
      }
      rows.push(row);
    }
    return rows;
  };

  const calGrid = buildCalGrid();

  // Columns for grid
  const columns = mode === 'day' ? DAYS_OF_WEEK : dateRange;
  const columnLabels = mode === 'day'
    ? DAY_LABELS
    : dateRange.map(d => {
        const dt = parseISO(d);
        return (
          <span key={d} className="flex flex-col items-center gap-0.5">
            <span className="text-[10px] text-slate-400">{format(dt, 'EEE')}</span>
            <span>{format(dt, 'd')}</span>
            <span className="text-[10px] text-slate-400">{format(dt, 'MMM')}</span>
          </span>
        );
      });

  const upcomingHolidays = holidays
    .filter(h => h.date >= format(new Date(), 'yyyy-MM-dd'))
    .sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Slot Management</h1>
        <p className="text-sm text-slate-500 mt-0.5">Configure pickup time slots (grid) and mark holiday dates (calendar)</p>
      </div>

      {/* ══ SLOT GRID ══════════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-[2rem] border border-slate-200/80 shadow-sm p-6">
        {/* Grid header row */}
        <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
          <div>
            <h2 className="font-black text-slate-800 text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Clock className="w-5 h-5 text-blue-500" /> Time Slot Grid
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">Click a cell to enable/disable a slot. Green = available to customers.</p>
          </div>

          {/* Mode toggle + Fix buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={handleFixAndCleanup} disabled={resetting} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors disabled:opacity-50">
              {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5" />}
              Fix &amp; Cleanup
            </button>
            <button onClick={handleResetAll} disabled={resetting} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition-colors disabled:opacity-50">
              {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
              Reset All
            </button>
            <div className="flex bg-slate-100 rounded-2xl p-1 gap-1">
              <button
                onClick={() => setMode('day')}
                className={`px-4 py-1.5 rounded-xl text-sm font-semibold transition-all ${mode === 'day' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              >
                By Day
              </button>
              <button
                onClick={() => setMode('date')}
                className={`px-4 py-1.5 rounded-xl text-sm font-semibold transition-all ${mode === 'date' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              >
                By Date
              </button>
            </div>

            {mode === 'date' && (
              <div className="flex items-center gap-1">
                <button onClick={() => setDateRangeStart(d => addDays(d, -7))} className="p-1.5 rounded-xl hover:bg-slate-100 transition-colors">
                  <ChevronLeft className="w-4 h-4 text-slate-500" />
                </button>
                <span className="text-xs font-semibold text-slate-600 min-w-[90px] text-center">
                  {format(addDays(dateRangeStart, 1), 'dd MMM')} – {format(addDays(dateRangeStart, 14), 'dd MMM')}
                </span>
                <button onClick={() => setDateRangeStart(d => addDays(d, 7))} className="p-1.5 rounded-xl hover:bg-slate-100 transition-colors">
                  <ChevronRight className="w-4 h-4 text-slate-500" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 mb-4 flex-wrap">
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <div className="w-5 h-5 rounded-lg bg-emerald-500 flex items-center justify-center text-white font-bold text-[10px]">✓</div>
            Active (visible to customers)
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <div className="w-5 h-5 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 font-bold text-[10px]">✕</div>
            Disabled
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <div className="w-5 h-5 rounded-lg border border-dashed border-slate-300 flex items-center justify-center text-slate-300 text-[10px]">+</div>
            Not configured (click to add)
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <div className="w-5 h-5 rounded-lg bg-red-50 border border-red-100 flex items-center justify-center">
              <AlertTriangle className="w-3 h-3 text-red-400" />
            </div>
            Holiday (all slots hidden)
          </div>
        </div>

        {/* Grid table */}
        <div className="overflow-x-auto">
          <table className="w-full border-separate" style={{ borderSpacing: '4px' }}>
            <thead>
              <tr>
                <th className="text-left text-xs font-bold text-slate-400 uppercase tracking-wide pl-2 pr-3 py-1 w-28">Time</th>
                {columns.map((col, ci) => {
                  const isHol = mode === 'date' && !!holidayMap[col];
                  return (
                    <th key={col} className="text-center min-w-[64px]">
                      <div className={`text-xs font-bold py-1 px-1 rounded-lg ${
                        isHol ? 'text-red-500 bg-red-50' :
                        mode === 'day' && col === format(new Date(), 'EEEE').toLowerCase() ? 'text-blue-600 bg-blue-50' :
                        'text-slate-600'
                      }`}>
                        {mode === 'day' ? DAY_LABELS[ci] : columnLabels[ci]}
                      </div>
                      {isHol && <div className="text-[9px] text-red-400 font-semibold mt-0.5">Holiday</div>}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loadingSlots ? (
                <tr>
                  <td colSpan={columns.length + 1} className="text-center py-10">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-500 mx-auto" />
                  </td>
                </tr>
              ) : TIME_SLOTS.map(timeSlot => (
                <tr key={timeSlot}>
                  <td className="text-xs font-semibold text-slate-600 pl-2 pr-3 py-0.5 whitespace-nowrap">{timeSlot}</td>
                  {columns.map(col => {
                    const isHol = mode === 'date' && !!holidayMap[col];
                    const cellKey = `${col}::${timeSlot}`;
                    const toggling = togglingCell === cellKey;
                    return (
                      <td key={col} className="py-0.5">
                        <SlotCell
                          slot={slotMap[col]?.[timeSlot]}
                          onToggle={() => !isHol && handleToggle(col, timeSlot)}
                          isHoliday={isHol}
                          loading={toggling}
                          defaultActive={mode === 'date'}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ══ HOLIDAY CALENDAR ═══════════════════════════════════════════════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-2 bg-white rounded-[2rem] border border-slate-200/80 shadow-sm p-6">
          <h2 className="font-black text-slate-800 text-lg flex items-center gap-2 mb-5" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Calendar className="w-5 h-5 text-red-400" /> Holiday Calendar
          </h2>

          {/* Month navigation */}
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => setCalMonth(m => subMonths(m, 1))} className="p-2 rounded-xl hover:bg-slate-100 transition-colors">
              <ChevronLeft className="w-4 h-4 text-slate-500" />
            </button>
            <span className="font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {format(calMonth, 'MMMM yyyy')}
            </span>
            <button onClick={() => setCalMonth(m => addMonths(m, 1))} className="p-2 rounded-xl hover:bg-slate-100 transition-colors">
              <ChevronRight className="w-4 h-4 text-slate-500" />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
              <div key={d} className="text-center text-[10px] font-black text-slate-400 uppercase tracking-wide py-1">{d}</div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="space-y-1">
            {calGrid.map((week, wi) => (
              <div key={wi} className="grid grid-cols-7 gap-1">
                {week.map((day, di) => (
                  <CalDayCell
                    key={di}
                    date={day}
                    currentMonth={calMonth}
                    holiday={holidayMap[format(day, 'yyyy-MM-dd')]}
                    selected={selectedCalDate?.date}
                    onSelect={handleCalSelect}
                  />
                ))}
              </div>
            ))}
          </div>

          {/* Selected date panel */}
          {selectedCalDate && (
            <div className="mt-4 rounded-2xl border p-4 animate-in slide-in-from-bottom-2">
              {selectedCalDate.existing ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-bold text-slate-800 text-sm">
                        {format(parseISO(selectedCalDate.date), 'EEEE, dd MMM yyyy')}
                      </p>
                      <p className="text-xs text-red-600 mt-0.5 font-medium">
                        Holiday: {selectedCalDate.existing.reason}
                      </p>
                    </div>
                    <button onClick={() => setSelectedCalDate(null)} className="text-slate-300 hover:text-slate-500">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <button
                    onClick={() => handleRemoveHoliday(selectedCalDate.existing)}
                    disabled={!!deletingHoliday}
                    className="flex items-center gap-2 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 text-sm font-semibold px-4 py-2 rounded-xl transition-colors"
                  >
                    {deletingHoliday ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                    Remove Holiday
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="font-bold text-slate-800 text-sm">
                      Mark {format(parseISO(selectedCalDate.date), 'EEEE, dd MMM yyyy')} as Holiday
                    </p>
                    <button onClick={() => setSelectedCalDate(null)} className="text-slate-300 hover:text-slate-500">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <input
                    type="text"
                    placeholder="Reason (e.g. Diwali, Sunday Off, Staff Training…)"
                    value={reasonInput}
                    onChange={e => setReasonInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleSaveHoliday()}
                    autoFocus
                    className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-400"
                  />
                  <p className="text-xs text-slate-400">Customers will see: "Week Off / Public Holiday – {reasonInput || '…'}"</p>
                  <button
                    onClick={handleSaveHoliday}
                    disabled={savingHoliday || !reasonInput.trim()}
                    className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white text-sm font-bold px-4 py-2 rounded-xl transition-colors disabled:opacity-50"
                  >
                    {savingHoliday ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
                    Mark as Holiday
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Upcoming holidays list */}
        <div className="bg-white rounded-[2rem] border border-slate-200/80 shadow-sm p-6">
          <h2 className="font-black text-slate-800 mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <AlertTriangle className="w-4 h-4 text-red-400" />
            Upcoming Holidays
            {upcomingHolidays.length > 0 && (
              <span className="text-xs font-semibold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{upcomingHolidays.length}</span>
            )}
          </h2>

          {loadingHolidays ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-red-400" /></div>
          ) : upcomingHolidays.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400 opacity-60" />
              <p className="text-sm">No upcoming holidays.</p>
              <p className="text-xs mt-1">Click any future date on the calendar to mark it.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {upcomingHolidays.map(h => (
                <div key={h.id} className="flex items-start gap-2 bg-red-50 border border-red-100 rounded-2xl px-3 py-2.5">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-red-700">
                      {format(parseISO(h.date), 'dd MMM yyyy, EEE')}
                    </p>
                    <p className="text-xs text-red-600 truncate mt-0.5">{h.reason}</p>
                  </div>
                  <button
                    onClick={() => handleRemoveHoliday(h)}
                    disabled={deletingHoliday === h.id}
                    className="text-red-200 hover:text-red-500 transition-colors flex-shrink-0 mt-0.5"
                  >
                    {deletingHoliday === h.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSlotManagement;
